import {t} from "@lingui/core/macro";
import {useCallback, useEffect} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {ImageSource} from "@khan/guide-types";

import {ALLOWED_FILE_TYPES} from "./constants.ts";
import {convertImageToDataURL} from "./utils/convert-image-to-data-url.ts";

const FILE_INPUT_ID = "attach-input";

interface Props {
    // A base64 encoded image to display in the preview.
    image: string | null;
    // Callback function to be called when an image is attached.
    onImageAttach?: (
        dataURL: string,
        source: ImageSource.ProvidedByFile,
    ) => void;
    // File dragged in dropzone
    fileDragged?: File;
    // Set the selected filename
    setSelectedFilename: (filename: string | null) => void;
}

/**
 * The ImageAttach component allows the user to attach a single image
 * and converts it to a data URL representing a scaled-down version of the image.
 */
export function ImageAttach({
    image,
    onImageAttach,
    fileDragged,
    setSelectedFilename,
}: Props): React.ReactElement {
    const handleFile = useCallback(
        (file: File) => {
            const reader = new FileReader();
            reader.onerror = (error) => {
                throw new Error(`Error reading file: ${error}`);
            };
            reader.onload = (event) => {
                setSelectedFilename(file.name);
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    try {
                        const scaledDataURL = convertImageToDataURL(img);
                        onImageAttach?.(
                            scaledDataURL,
                            ImageSource.ProvidedByFile,
                        );
                    } catch (err) {
                        setSelectedFilename(null);
                        throw new Error(`Error processing image: ${err}`);
                    } finally {
                        const fileInput =
                            document.getElementById(FILE_INPUT_ID);
                        if (fileInput && "value" in fileInput) {
                            fileInput.value = "";
                        }
                    }
                };
            };
            reader.readAsDataURL(file);
        },
        [onImageAttach, setSelectedFilename],
    );

    useEffect(() => {
        const fileInput = document.getElementById(FILE_INPUT_ID);
        if (!fileInput) {
            throw new Error("File input element not found");
        }

        const changeListener = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                throw new Error("No file selected");
            }
            handleFile(file);
        };
        const cancelListener = () => {
            setSelectedFilename(null);
        };
        fileInput.addEventListener("change", changeListener);
        return () => {
            fileInput.removeEventListener("change", changeListener);
            fileInput.removeEventListener("cancel", cancelListener);
        };
    }, [onImageAttach, fileDragged, handleFile, setSelectedFilename]);

    useEffect(() => {
        if (fileDragged) {
            handleFile(fileDragged);
        }
    }, [fileDragged, handleFile]);

    const handleAttachClick = () => {
        const attachInput = document.getElementById(FILE_INPUT_ID);
        if (attachInput) {
            attachInput.click();
        }
    };

    return (
        <>
            {/* These kooky gymnastics are necessary because while we can style the  */}
            {/* HTML Input button pretty well with CSS, we can't internationalize    */}
            {/* the text, so we're hiding the native button and adding our own, that */}
            {/* then delegates click events to the hidden native control.            */}
            {/* Also, input requires the styles to be inlined, or at least,          */}
            {/* they don't work with Aphrodite StyleSheet.                           */}
            <input
                type="file"
                id="attach-input"
                accept={ALLOWED_FILE_TYPES.join(", ")}
                style={{display: "none"}}
            />
            <label htmlFor="attach-input">
                <Button
                    disabled={!!image}
                    kind="tertiary"
                    onClick={handleAttachClick}
                    aria-label={t`Choose an image to attach`}
                >
                    {t`Choose file`}
                </Button>
            </label>
        </>
    );
}
